import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import {
  CUSTOMER_STATUS,
  FUNCTION_RESULT_STATUS
} from '@nova/common-interfaces';
import { ICustomerAccount } from '@rfm/shared-interfaces';
import { BlockUIModule } from 'ng-block-ui';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { MenuModule } from 'primeng/menu';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { CommonComponent } from '../../../../../lib/components/index.mjs';
import { BoolToTextPipe } from '../../../../../lib/pipes/bool-to-text.pipe.mjs';
import { CustomerService } from '../../../../shared/services/customers/customers.service.mjs';

interface Column {
  field: string;
  header: string;
  type: string;
  pipe: string | undefined;
}

@Component({
  selector: 'app-customer-payment-processing',
  standalone: true,
  imports: [
    TableModule,
    CommonModule,
    FormsModule,
    RouterLink,
    ReactiveFormsModule,
    BlockUIModule,
    ToastModule,
    BoolToTextPipe,
    ConfirmDialogModule,
    ButtonModule,
    MenuModule,
    DialogModule,
    SelectButtonModule,
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './customer-due-payment-processing.component.html',
  styleUrls: ['./customer-due-payment-processing.component.scss'], // Fixed typo
})
export class CustomerDuePaymentProcessingComponent extends CommonComponent implements OnInit {
  cols: Column[] = [];
  customers: ICustomerAccount[] | [] = [];
  filtered_customers: ICustomerAccount[] = [];
  activeItem: ICustomerAccount | undefined;

  objectType = signal('customer');
  objectId = signal('');
  value: string = 'off';

  buttonChanged(event: any) {
    this.applyFilter();
  }

  applyFilter() {
    if (this.value === 'active') {
      this.filtered_customers = this.customers.filter(
        (x) => x.status === CUSTOMER_STATUS.ACTIVE
      );
    } else if (this.value === 'inactive') {
      this.filtered_customers = this.customers.filter(
        (x) => x.status === CUSTOMER_STATUS.INACTIVE
      );
    } else if (this.value === 'lead') {
      this.filtered_customers = this.customers.filter(
        (x) => x.status === CUSTOMER_STATUS.LEAD
      );
    } else if (this.value === 'suspended') {
      this.filtered_customers = this.customers.filter(
        (x) => x.status === CUSTOMER_STATUS.SUSPENDED
      );
    } else {
      this.filtered_customers = this.customers;
    }
  }

  row_items: MenuItem[] = [];
  items: MenuItem[] = [
    {
      label: 'View',
      icon: 'pi pi-file',
      command: () => this.view(this.activeItem!.id),
    },
    {
      label: 'Edit',
      id: 'edit',
      icon: 'pi pi-file-edit',
      command: () => this.edit(this.activeItem!.id),
    },
    {
      label: 'Suspend',
      id: 'suspend',
      icon: 'pi pi-pause-circle',
      command: () => this.suspend(this.activeItem!.id),
    },
    {
      label: 'Cancel',
      id: 'cancel',
      icon: 'pi pi-times-circle',
      command: () => this.cancel(this.activeItem!.id),
    },
 ];

  constructor(
    private customerService: CustomerService,
    protected router: Router,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {
    super();
    this.isLoading(true);
    this.cols.push({
      field: 'first_name',
      header: 'First Name',
      type: 'text',
    } as Column);
    this.cols.push({
      field: 'last_name',
      header: 'Last Name',
      type: 'text',
    } as Column);
    this.cols.push({
      field: 'status',
      header: 'Status',
      type: 'text',
    } as Column);
    this.cols.push({
      field: 'autopay_status',
      header: 'Autopay',
      pipe: 'boolToText',
      type: 'boolean',
    } as Column);
    this.cols.push({
      field: 'subscriptions_total',
      header: 'Monthly $',
      type: 'numeric',
    } as Column);
    this.cols.push({
      field: 'current_balance',
      header: 'Balance',
      type: 'numeric',
    } as Column);
  }

  selectedCustomers = signal<ICustomerAccount[] | undefined>(undefined);

  handleData(data: any) {
    this.customerService.getAllWithBalance()
      .then((result) => {
        if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
          this.customers = result.data ?? []; // Safely handle undefined data
          this.applyFilter();
        } else {
          console.error('Failed to fetch customers:', result.status);
        }
      })
      .catch((error) => {
        console.error('Error fetching customers:', error);
      })
      .finally(() => {
        this.isLoading(false);
      });
  }

  rowSelected(event: any) {
    this.objectId.set(event.id!);
  }

  menuClicked(rowData: ICustomerAccount) {
    this.activeItem = rowData;
    this.row_items = [];

    this.items.forEach((item) => {
      switch (item.id) {
        case 'suspend':
          if (this.activeItem?.status == CUSTOMER_STATUS.ACTIVE) {
            this.row_items.push(item);
          }
          return item;
        case 'activate':
          if (
            this.activeItem?.status == CUSTOMER_STATUS.SUSPENDED ||
            this.activeItem?.status == CUSTOMER_STATUS.LEAD
          ) {
            this.row_items.push(item);
          }
          return item;
        case 'cancel':
          if (
            this.activeItem?.status === CUSTOMER_STATUS.ACTIVE ||
            this.activeItem?.status === CUSTOMER_STATUS.SUSPENDED
          ) {
            this.row_items.push(item);
          }
          return item;
        case 'change_price':
          if (
            this.activeItem?.status === CUSTOMER_STATUS.ACTIVE ||
            this.activeItem?.status === CUSTOMER_STATUS.SUSPENDED ||
            this.activeItem?.status === CUSTOMER_STATUS.LEAD
          ) {
            this.row_items.push(item);
          }
          return item;
        case 'change_plan':
          if (
            this.activeItem?.status === CUSTOMER_STATUS.ACTIVE ||
            this.activeItem?.status === CUSTOMER_STATUS.SUSPENDED ||
            this.activeItem?.status === CUSTOMER_STATUS.LEAD
          ) {
            this.row_items.push(item);
          }
          return item;
        default:
          this.row_items.push(item);
          return item;
      }
    });
    this.objectId.set(rowData.id);
  }

  view(customer_id: string) {
    this.router.navigateByUrl('/admin/customers/view/' + customer_id);
  }

  edit(customer_id: string) {
    this.router.navigateByUrl('/admin/customers/edit/' + customer_id);
  }

  activate(customer_id: string) {
    this.confirmationService.confirm({
      target: event!.target as EventTarget,
      message: 'Are you sure that you want to activate this customer?',
      header: 'Activate Customer',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Activate Customer',
      },
      accept: async () => {
        //*************************************
        //TODO: add workflow to credit and debit customer account for plan change
        // */
        this.isLoading(true);

        await this.customerService
          .activate(customer_id)
          .then((result) => {
            if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
              this.messageService.add({
                severity: 'error',
                summary: 'Customer was not activated',
                detail: 'Customer has not been changed',
                life: 3000,
              });
              this.isLoading(false);
              return Promise.reject(result);
            }

            this.messageService.add({
              severity: 'success',
              summary: 'Customer Activated',
              detail: 'Customer has been activated successfully!',
              life: 3000,
            });
            this.isLoading(false);
            this.handleData(true);
            return Promise.resolve();
          })
          .catch((err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Customer Not Activated',
              detail: 'Customer has not been activated',
              life: 3000,
            });
            this.isLoading(false);
            return Promise.reject(err);
          })
          .finally(() => {
            this.isLoading(false);
          });
      },
      reject: () => {
        this.isLoading(false);
      },
    });
  }

  suspend(customer_id: string) {
    this.confirmationService.confirm({
      target: event!.target as EventTarget,
      message: 'Are you sure that you want to suspend this customer?',
      header: 'Suspend Customer',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Suspend Customer',
      },
      accept: async () => {
        //*************************************
        //TODO: add workflow to credit and debit customer account for plan change
        // */
        this.isLoading(true);

        await this.customerService
          .suspend(customer_id)
          .then((result) => {
            if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
              this.messageService.add({
                severity: 'error',
                summary: 'Customer was not suspended',
                detail: 'Customer has not been changed',
                life: 3000,
              });
              this.isLoading(false);
              return Promise.reject(result);
            }

            this.messageService.add({
              severity: 'success',
              summary: 'Customer Suspended',
              detail: 'Customer has been suspended successfully!',
              life: 3000,
            });
            this.isLoading(false);
            this.handleData(true);
            return Promise.resolve();
          })
          .catch((err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Customer Not Suspended',
              detail: 'Customer has not been suspended',
              life: 3000,
            });
            this.isLoading(false);
            return Promise.reject(err);
          })
          .finally(() => {
            this.isLoading(false);
          });
      },
      reject: () => {
        this.isLoading(false);
      },
    });
  }

  cancel(customer_id: string) {
    this.confirmationService.confirm({
      target: event!.target as EventTarget,
      message: 'Are you sure that you want to delete this customer?',
      header: 'Delete Customer',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Delete Customer',
      },
      accept: async () => {
        //*************************************
        //TODO: add workflow to credit and debit customer account for plan change
        // */
        this.isLoading(true);

        await this.customerService
          .cancel(customer_id)
          .then((result) => {
            if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
              this.messageService.add({
                severity: 'error',
                summary: 'Customer was not deleted',
                detail: 'Customer has not been changed',
                life: 3000,
              });
              this.isLoading(false);
              return Promise.reject(result);
            }

            this.messageService.add({
              severity: 'success',
              summary: 'Customer Deleted',
              detail: 'Customer has been deleted successfully!',
              life: 3000,
            });
            this.isLoading(false);
            this.handleData(true);
            return Promise.resolve();
          })
          .catch((err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Customer Not Deleted',
              detail: 'Customer has not been deleted',
              life: 3000,
            });
            this.isLoading(false);
            return Promise.reject(err);
          })
          .finally(() => {
            this.isLoading(false);
          });
      },
      reject: () => {
        this.isLoading(false);
      },
    });
  }

  ngOnInit(): void {
    this.handleData(true);
  }
}
